// src/budget/utils/programRowUtils.js

/**
 * List of all recognized budget-block names.
 * These correspond to top-level categories inside each
 * Approved/Actual/Proposed program-year node in Firestore.
 *
 * Blocks not present in a given year are normalized into empty objects.
 * This ensures downstream lookup logic never needs to guard for undefined blocks.
 *
 * NOTE:
 * "Grant" is intentionally NOT a block here. In your data, grants are represented
 * as an Offset (e.g., Offsets: { Grant: -43839 }).
 *
 * @type {string[]}
 */
const BLOCK_KEYS = [
  // Board of Education Items
  "Positions",
  "Salaries",
  "Purchased Services",
  "Supplies / Materials",
  "Program Development",
  "Staff Development",
  "Program / Staff Development",
  "Employee Benefits",
  "Other",
  "Utilities",
  "Equipment",
  "Offsets",

  // Town Items
  "Items", 
  "Tax Revenues",
  "Licenses & Permits",
  "HousAuth In-Lieu Tax",
  "ST Educ Entitlements",
  "Administrative Services",
  "Public Safety",
  "Human Services",
  "Charges for Services",
  "Other Revenues",
  "Transfers In From",
  "General Government",
  "Physical Services",
  "Sanitation",
  "Debt Transfers Out",
  "Debt Service"
];

/**
 * Retrieve a field value from a program-year section by walking the fieldDef.sources list.
 *
 * A fieldDef may define multiple fallback lookup locations:
 * [
 *   { block: "Salaries", key: "Teachers" },
 *   { block: "Salaries", key: "Teachers (English)" }
 * ]
 *
 * The first block/key pair whose key exists is returned.
 * If no source matches, null is returned.
 *
 * @param {Object} section
 *   A normalized section object containing all possible BLOCK_KEYS.
 *
 * @param {Object} fieldDef
 *   A field definition from config.fieldDefs.
 *   @property {Array<{block: string, key: string}>} fieldDef.sources
 *     Ordered list of potential lookup paths.
 *
 * @returns {* | null}
 *   The retrieved value, or null if not found.
 */
function getFieldValue(section, fieldDef) {
  if (!fieldDef.sources || !fieldDef.sources.length) return null;

  for (const { block, key } of fieldDef.sources) {
    const blockData = section[block] || {};
    if (Object.prototype.hasOwnProperty.call(blockData, key)) {
      return blockData[key];
    }
  }

  return null;
}

/**
 * Build a list of budget rows (one per year) for a given program and section
 * (e.g., "Approved", "Actual", "Proposed").
 *
 * ### Behavior:
 * - Only years with a matching sectionKey (Approved/Actual/etc.) are considered.
 * - All BLOCK_KEYS are normalized into empty objects to prevent undefined lookups.
 * - Each fieldDef in config is resolved using `getFieldValue`.
 * - A row is included **only if at least one field contains a non-null value**.
 *   Completely empty years are automatically excluded.
 *
 * ### Extra metadata (new):
 * - `_offsetRules` is copied from the section (if present) so the snapshot/table layer
 *   can decide whether specific offsets should count toward totals and can render notes.
 *
 * ### Example Output Row:
 * {
 *   label: "2024",
 *   year: 2024,
 *   salariesTeachers: 1_669_959,
 *   positionsTeachers: 23,
 *   _offsetRules: { Grant: { countInTotals: false, note: "..." } }
 * }
 *
 * @param {Object} data
 *   The entire Firestore document for a program (e.g., Program 0500, Program 3400),
 *   containing year keys like "2019", "2020", "2021".
 *
 * @param {Array<Object>} fieldDefs
 *   Array of field definition objects from the program component's config.
 *
 * @param {string} sectionKey
 *   The section name to extract rows for (e.g., "Approved", "Actual", "Proposed").
 *
 * @returns {Array<Object>}
 *   A list of row objects suitable for rendering by ProgramBudget.
 */
export function buildProgramRowsFromDoc(data, fieldDefs, sectionKey) {
  if (!data) return [];

  // Identify all 4-digit year keys
  const yearKeys = Object.keys(data)
    .filter((k) => /^\d{4}$/.test(k))
    .sort((a, b) => Number(a) - Number(b));

  const numericFieldKeys = new Set(
    fieldDefs
      .filter((fd) => fd.valueType === "currency" || fd.valueType === "count")
      .map((fd) => fd.key)
  );

  const rows = [];

  for (const year of yearKeys) {
    const yearNode = data[year] || {};
    const section = yearNode[sectionKey];

    // Skip years that do not have this section (e.g., no Approved block for earlier years)
    if (!section) continue;

    /**
     * Normalize the section into a predictable shape WITHOUT DROPPING unknown blocks.
     *
     * Key change:
     * - Start from { ...section } so blocks like "St Conn In-Lieu Taxes" survive.
     * - Then ensure classic BLOCK_KEYS exist (so BOE program docs still behave).
     */
    const normalizedSection = { ...(section || {}) };
    BLOCK_KEYS.forEach((block) => {
      if (!normalizedSection[block]) normalizedSection[block] = {};
    });

    const row = {
      label: year,
      year: Number(year),

      // carry rule metadata forward for snapshot/table logic
      _offsetRules: section.OffsetRules || section.offsetRules || {},
    };

    let hasValue = false;

    // Populate row using fieldDefs lookup
    for (const fieldDef of fieldDefs) {
      const rawValue = getFieldValue(normalizedSection, fieldDef);

      // Decide if this year has any real data (raw semantics)
      if (
        rawValue !== null &&
        rawValue !== undefined &&
        rawValue !== "" &&
        !(typeof rawValue === "number" && Number.isNaN(rawValue))
      ) {
        hasValue = true;
      }

      // Store value for rendering / math
      if (rawValue === undefined || rawValue === null) {
        // Default missing numeric fields to 0
        row[fieldDef.key] = numericFieldKeys.has(fieldDef.key) ? 0 : null;
      } else {
        row[fieldDef.key] = rawValue;
      }
    }

    // Include rows that have real fields OR have meaningful offset rules (even if values are sparse)
    const hasOffsetRules =
      row._offsetRules && Object.keys(row._offsetRules).length > 0;

    if (hasValue || hasOffsetRules) {
      rows.push(row);
    }
  }

  return rows;
}


